import React, { useEffect, useRef } from 'react';
import { pickEmberColor, glowSprite, flameColor, flameSprite, drawGasBlob } from '../../utils/emberPalette';
// Golden ember particles drifting in the void; drawn toward the focused nav item.
// On focus, a burst of gas shoots out from the source element toward the button.
// stream=true: thin horizontal spark stream (used in the portfolio topbar).
const EmberField = ({ focus = null, count, stream = false, sourceSel = '.xp-head', targetPrefix = 'xp-nav-' }) => {
  const canvasRef = useRef(null);
  const focusRef = useRef(focus);
  focusRef.current = focus;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let w = 0;
    let h = 0;
    let raf;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const N = count || (window.innerWidth <= 768 ? 40 : 90);
    let pmx = 0;
    let pmy = 0;
    const onMouse = (e) => {
      pmx = e.clientX / window.innerWidth - 0.5;
      pmy = e.clientY / window.innerHeight - 0.5;
    };
    if (!stream) window.addEventListener('mousemove', onMouse);
    const spawn = (fromEdge = false) =>
      stream
        ? {
            x: fromEdge ? -10 : Math.random() * w,
            y: 4 + Math.random() * Math.max(1, h - 8),
            r: 0.5 + Math.random() * 1.3,
            vx: 0.35 + Math.random() * 0.6,
            vy: 0,
            tw: Math.random() * Math.PI * 2,
            tws: 0.02 + Math.random() * 0.03,
            col: pickEmberColor(),
          }
        : {
            x: Math.random() * w,
            y: fromEdge ? h + 10 : Math.random() * h,
            r: 0.6 + Math.random() * 1.8,
            vx: (Math.random() - 0.5) * 0.15,
            vy: -(0.08 + Math.random() * 0.28),
            tw: Math.random() * Math.PI * 2,
            tws: 0.01 + Math.random() * 0.025,
            col: pickEmberColor(),
            gas: Math.random() < 0.6,
            z: 0.45 + Math.random() * 1.15,
          };
    const parts = Array.from({ length: N }, () => spawn());

    let target = null;
    const updateTarget = () => {
      const f = focusRef.current;
      if (!f) {
        target = null;
        return;
      }
      const el = document.querySelector(`[data-testid="${targetPrefix}${f}"]`);
      if (!el) {
        target = null;
        return;
      }
      const cb = canvas.getBoundingClientRect();
      const eb = el.getBoundingClientRect();
      target = { x: eb.left + eb.width / 2 - cb.left, y: eb.top + eb.height / 2 - cb.top };
    };

    // --- Ember burst: sparks shooting from the head toward the focused button ---
    const burstParts = [];
    let burstKey = null;
    let emitCooldown = 0;

    const headCenter = () => {
      const headEl = document.querySelector(sourceSel);
      if (!headEl) return null;
      const cb = canvas.getBoundingClientRect();
      const hb = headEl.getBoundingClientRect();
      return {
        x: hb.left + hb.width / 2 - cb.left,
        y: hb.top + hb.height / 2 - cb.top,
        rx: hb.width * 0.32,
        ry: hb.height * 0.38,
      };
    };

    let absorbGlow = 0;
    let hoverFrames = 0;

    const emitBurst = (n, big, boost = 1) => {
      if (!target) return;
      const hc = headCenter();
      if (!hc) return;
      const heat = Math.min(1, hoverFrames / 210);
      const base = Math.atan2(target.y - hc.y, target.x - hc.x);
      for (let i = 0; i < n; i++) {
        // ignite on the head's surface facing the button; digs deeper the longer the hover
        const ang = base + (Math.random() - 0.5) * (1.25 + heat * 0.9);
        const rr = 0.55 - heat * 0.3 + Math.random() * (0.5 + heat * 0.35);
        const x = hc.x + Math.cos(ang) * hc.rx * rr;
        const y = hc.y + Math.sin(ang) * hc.ry * rr;
        const tx = target.x + (Math.random() - 0.5) * 12;
        const ty = target.y + (Math.random() - 0.5) * 8;
        burstParts.push({
          x,
          y,
          vx: Math.cos(ang) * (0.15 + Math.random() * 0.35),
          vy: Math.sin(ang) * (0.15 + Math.random() * 0.35),
          tx,
          ty,
          d0: Math.hypot(tx - x, ty - y) || 1,
          life: 0,
          detach: 8 + Math.random() * 14,
          flicker: !big && Math.random() < 0.3 + heat * 0.25,
          kicked: false,
          maxLife: (46 + Math.random() * 40) * boost,
          r: 0.7 + Math.random() * (1.9 + heat * 1.1),
          curl: (Math.random() < 0.5 ? -1 : 1) * (0.2 + Math.random() * 0.28),
          tw: Math.random() * Math.PI * 2,
          tws: 0.07 + Math.random() * 0.09,
          trail: [{ x, y, c: flameColor(0) }],
        });
      }
    };

    const onExplode = () => emitBurst(60, true, 1.7);
    window.addEventListener('xp-burst', onExplode);

    const drawBursts = () => {
      if (stream) return;
      const f = focusRef.current;
      if (f && f !== burstKey && target) {
        burstKey = f;
        hoverFrames = 0;
        emitBurst(16, true);
        emitCooldown = 0;
      }
      if (!f) {
        burstKey = null;
        hoverFrames = 0;
      }
      if (f && target) {
        hoverFrames += 1;
        const heat = Math.min(1, hoverFrames / 210);
        emitCooldown -= 1;
        if (emitCooldown <= 0) {
          emitBurst(2 + Math.round(heat * 4), false);
          emitCooldown = 5 - Math.round(heat * 2);
        }
      }
      // accumulated absorption glow on the hovered button (warm core + violet halo)
      if (target && absorbGlow > 0.02) {
        const e = Math.min(absorbGlow, 1);
        const g2 = ctx.createRadialGradient(target.x, target.y, 0, target.x, target.y, 105);
        g2.addColorStop(0, `rgba(170, 105, 255, ${e * 0.22})`);
        g2.addColorStop(1, 'rgba(170, 105, 255, 0)');
        ctx.beginPath();
        ctx.arc(target.x, target.y, 105, 0, Math.PI * 2);
        ctx.fillStyle = g2;
        ctx.fill();
        const g = ctx.createRadialGradient(target.x, target.y, 0, target.x, target.y, 62);
        g.addColorStop(0, `rgba(255, 160, 90, ${e * 0.32})`);
        g.addColorStop(1, 'rgba(255, 160, 90, 0)');
        ctx.beginPath();
        ctx.arc(target.x, target.y, 62, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
      }
      absorbGlow *= 0.93;
      for (let i = burstParts.length - 1; i >= 0; i--) {
        const p = burstParts[i];
        const dx = p.tx - p.x;
        const dy = p.ty - p.y;
        const d = Math.hypot(dx, dy) || 1;
        p.tw += p.tws;
        p.life += 1;
        if (p.life < p.detach) {
          // igniting blue on the head surface: smolder in place with tiny jitter
          p.x += (Math.random() - 0.5) * 0.6;
          p.y += (Math.random() - 0.5) * 0.6;
          const ia = (p.life / p.detach) * 0.9;
          const s = p.r * (4 + Math.random() * 2);
          ctx.globalAlpha = ia;
          ctx.drawImage(flameSprite(Math.random() * 0.15), p.x - s / 2, p.y - s / 2, s, s);
          ctx.globalAlpha = 1;
          p.trail.length = 0;
          p.trail.push({ x: p.x, y: p.y, c: flameColor(0), j: 0 });
          continue;
        }
        if (p.flicker) {
          // fragment that just crumbles and dies on the head
          const fa = Math.max(0, 1 - (p.life - p.detach) / 12) * 0.8;
          if (fa <= 0) {
            burstParts.splice(i, 1);
            continue;
          }
          p.y += 0.25;
          const s = p.r * 4.5;
          ctx.globalAlpha = fa;
          ctx.drawImage(flameSprite(0.1), p.x - s / 2, p.y - s / 2, s, s);
          ctx.globalAlpha = 1;
          continue;
        }
        if (!p.kicked) {
          // break off the head toward the button
          p.kicked = true;
          const k = 1.6 + Math.random() * 2.2;
          p.vx = (dx / d) * k + (Math.random() - 0.5) * 1.2;
          p.vy = (dy / d) * k + (Math.random() - 0.5) * 1.2;
        }
        const absorbing = d < 48;
        if (absorbing) {
          // drawn into the button: strong pull, decelerate, shrink
          p.vx = p.vx * 0.82 + (dx / d) * 1.4;
          p.vy = p.vy * 0.82 + (dy / d) * 1.4;
          p.r *= 0.955;
        } else {
          p.vx += (dx / d) * 0.34;
          p.vy += (dy / d) * 0.34;
          const wob = Math.sin(p.tw) * p.curl;
          p.vx += (-dy / d) * wob;
          p.vy += (dx / d) * wob;
        }
        const sp = Math.hypot(p.vx, p.vy);
        if (sp > 5.6) {
          p.vx = (p.vx / sp) * 5.6;
          p.vy = (p.vy / sp) * 5.6;
        }
        p.x += p.vx;
        p.y += p.vy;
        const j = Math.min(Math.max(1 - d / p.d0, 0), 1);
        p.trail.push({ x: p.x, y: p.y, c: flameColor(j), j });
        if (p.trail.length > 6) p.trail.shift();
        if (p.life > p.maxLife || d < 10) {
          if (d < 10) absorbGlow += 0.12;
          burstParts.splice(i, 1);
          continue;
        }
        // flowing galaxy gas: stretched soft plasma blobs along the flight path
        const a = (1 - p.life / p.maxLife) * 0.8;
        const ang = Math.atan2(p.vy, p.vx);
        const grow = 1 + (p.life / p.maxLife) * 1.6;
        for (let t = 0; t < p.trail.length; t++) {
          const tp = p.trail[t];
          const tf = (t + 1) / p.trail.length;
          drawGasBlob(
            ctx,
            flameSprite(tp.j !== undefined ? tp.j : j),
            tp.x,
            tp.y,
            p.r * 8.5 * grow * (0.5 + 0.5 * tf),
            ang,
            2.1,
            a * tf * 0.7
          );
        }
        // hot star core at the tip
        ctx.globalAlpha = a;
        const cs = p.r * 3;
        ctx.drawImage(flameSprite(j), p.x - cs / 2, p.y - cs / 2, cs, cs);
        ctx.globalAlpha = 1;
      }
    };

    const tick = () => {
      updateTarget();
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';
      drawBursts();
      for (const p of parts) {
        p.tw += p.tws;
        if (stream) {
          p.x += p.vx;
          p.y += Math.sin(p.tw) * 0.18;
          if (p.x > w + 10) Object.assign(p, spawn(true));
        } else {
          const zz = p.z || 1;
          p.x += (p.vx + Math.sin(p.tw) * 0.12) * zz;
          p.y += p.vy * zz;
          if (target) {
            const dx = target.x - p.x;
            const dy = target.y - p.y;
            const d2 = dx * dx + dy * dy;
            if (d2 < 260 * 260 && d2 > 30 * 30) {
              const d = Math.sqrt(d2);
              p.x += (dx / d) * 1.5;
              p.y += (dy / d) * 1.5;
            }
          }
          if (p.y < -10 || p.x < -10 || p.x > w + 10) {
            Object.assign(p, spawn(true));
          }
        }
        const a = Math.max(0.22 + Math.sin(p.tw) * 0.2, 0.05);
        if (!stream && p.gas) {
          // drifting nebula mote with 3D depth parallax
          const zz = p.z || 1;
          const dx3 = pmx * (zz - 1) * 70;
          const dy3 = pmy * (zz - 1) * 70;
          drawGasBlob(ctx, glowSprite(p.col), p.x + dx3, p.y + dy3, p.r * 12 * zz, p.tw * 0.35, 1.7, a * 0.45 * Math.min(zz, 1.2));
        } else if (!stream) {
          const zz = p.z || 1;
          const dx3 = pmx * (zz - 1) * 70;
          const dy3 = pmy * (zz - 1) * 70;
          const s = p.r * 5 * zz;
          ctx.globalAlpha = a * Math.min(zz, 1.2);
          ctx.drawImage(glowSprite(p.col), p.x + dx3 - s / 2, p.y + dy3 - s / 2, s, s);
          ctx.globalAlpha = 1;
        } else {
          const s = p.r * 5;
          ctx.globalAlpha = a;
          ctx.drawImage(glowSprite(p.col), p.x - s / 2, p.y - s / 2, s, s);
          ctx.globalAlpha = 1;
        }
      }
      ctx.globalCompositeOperation = 'source-over';
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('xp-burst', onExplode);
      if (!stream) window.removeEventListener('mousemove', onMouse);
    };
  }, []);

  return <canvas ref={canvasRef} className="xp-embers" aria-hidden="true" data-testid="xp-embers" />;
};

export default EmberField;
;
